// Streaming decoder for the WHP 198 audio description data channel. Used to
// self-check exported control tracks and in tests. The approach follows the
// BBC gst-audiodescription whp198dec element: recover the bit clock from
// zero crossings, read a bit at each mid-bit crossing (falling = 1), find the
// AD_descriptor header, and validate the CRC. Inverted signals are accepted,
// as §4 asks of practical decoders.

import { BIT_RATE, PACKET_BITS, DESCRIPTOR_HEADER } from "./constants.js";
import { crc16 } from "./crc.js";

const HEADER_BITS = 48;
const HEADER_MOD = 2 ** HEADER_BITS;
const HEADER_VALUE = DESCRIPTOR_HEADER.slice(0, 6).reduce((acc, byte) => acc * 256 + byte, 0);
const HEADER_INVERTED = HEADER_MOD - 1 - HEADER_VALUE;

/**
 * @typedef {object} DecodedPacket
 * @property {Uint8Array} bytes
 * @property {boolean} crcOk
 * @property {number} fade
 * @property {number} pan
 * @property {number} startSample - approximate sample position of the first bit
 * @property {boolean} inverted
 */

export class Whp198Decoder {
    constructor(sampleRate = 48000) {
        this.bitSamples = sampleRate / BIT_RATE;
        this.tolerance = this.bitSamples * 0.2;
        this.position = 0;
        this.prev = 0;
        this.lastCrossing = null;
        this.expectedMid = null;
        this.window = 0;
        this.collecting = null;
    }

    /**
     * @param {ArrayLike<number>} samples
     * @returns {DecodedPacket[]}
     */
    push(samples) {
        const packets = [];
        for (let i = 0; i < samples.length; i++) {
            const n = this.position + i;
            const cur = samples[i];
            const prev = this.prev;
            if ((prev < 0 && cur >= 0) || (prev >= 0 && cur < 0)) {
                const crossing = n - 1 + (prev === cur ? 0 : prev / (prev - cur));
                this.#onCrossing(crossing, cur < 0, packets);
            } else if (this.expectedMid !== null && n > this.expectedMid + this.tolerance) {
                this.#loseSync();
            }
            this.prev = cur;
        }
        this.position += samples.length;
        return packets;
    }

    #loseSync() {
        this.expectedMid = null;
        this.collecting = null;
        this.window = 0;
    }

    #onCrossing(crossing, falling, packets) {
        const T = this.bitSamples;
        if (this.expectedMid !== null) {
            const d = crossing - this.expectedMid;
            if (Math.abs(d) <= this.tolerance) {
                this.expectedMid = crossing + T;
                this.#onBit(falling ? 1 : 0, crossing, packets);
                return;
            }
            if (Math.abs(d + T / 2) <= this.tolerance) return; // bit boundary
            this.#loseSync();
        }
        // Two crossings a whole bit apart are both mid-bit crossings.
        if (this.lastCrossing !== null && Math.abs(crossing - this.lastCrossing - T) <= this.tolerance) {
            this.expectedMid = crossing + T;
            this.#onBit(falling ? 1 : 0, crossing, packets);
        }
        this.lastCrossing = crossing;
    }

    #onBit(bit, crossing, packets) {
        this.lastCrossing = crossing;
        if (this.collecting) {
            const c = this.collecting;
            c.bits.push(c.inverted ? bit ^ 1 : bit);
            if (c.bits.length === PACKET_BITS) {
                packets.push(toPacket(c));
                this.collecting = null;
                this.window = 0;
            }
            return;
        }
        this.window = (this.window % (HEADER_MOD / 2)) * 2 + bit;
        const inverted = this.window === HEADER_INVERTED;
        if (this.window === HEADER_VALUE || inverted) {
            const bits = [];
            for (let b = HEADER_BITS - 1; b >= 0; b--) {
                bits.push(Math.floor(HEADER_VALUE / 2 ** b) % 2);
            }
            this.collecting = {
                bits,
                inverted,
                startSample: crossing - (HEADER_BITS - 0.5) * this.bitSamples,
            };
        }
    }
}

function toPacket({ bits, inverted, startSample }) {
    const bytes = new Uint8Array(PACKET_BITS / 8);
    bits.forEach((bit, i) => {
        bytes[i >> 3] |= bit << (7 - (i & 7));
    });
    return {
        bytes,
        crcOk: crc16(bytes) === 0,
        fade: bytes[7],
        pan: bytes[8],
        startSample,
        inverted,
    };
}
